import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, unref as _unref, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withCtx as _withCtx, createBlock as _createBlock, withDirectives as _withDirectives, createVNode as _createVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex items-center" }
const _hoisted_2 = { class: "text-gray-90 text-lg font-medium" }
const _hoisted_3 = { class: "ml-6" }
const _hoisted_4 = { class: "min-h-[350px] columns-2 gap-x-12" }
const _hoisted_5 = { class: "flex flex-1 justify-between gap-1 text-sm font-medium" }
const _hoisted_6 = { class: "flex-1" }
const _hoisted_7 = { key: 0 }

import { computed, ref, onBeforeMount } from 'vue'
import { UiPillToggle, UiTag, UiButton } from '@klausapp/ui-kit'
import type { UiTagThemeType } from '@klausapp/ui-kit/types'
import dayjs from 'dayjs'

import { oldApi } from '@/api'
import QModal from '@/components/QModal.vue'
import { writeOverrides } from '@/utils/feature-flag-override'
import { session } from '@/composables/useSession'
import type { Features } from '@/types/session'

type Tags = 'plan' | 'addon' | 'data' | 'dev' | 'custom'

interface FeatureFlag {
  id: string
  name: keyof Features
  value: boolean
  description: string
  tags: { value: Tags; tooltip: string }[]
  accountTooltip: string | null
  localValue?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FlagManager',
  emits: ["close"],
  setup(__props) {



const tagColors: Record<Tags, UiTagThemeType> = {
  plan: 'brand',
  addon: 'blue',
  data: 'green',
  dev: 'yellow',
  custom: 'default',
}

const tagTooltips = {
  plan: 'Included with pricing plan',
  addon: 'Included with addon',
  data: 'This feature requires data analysis',
  dev: 'This feature is in development',
  custom: 'Custom development for a client',
}

const tagFilter = ref<Record<Tags, boolean>>({ dev: true, plan: false, addon: false, data: false, custom: false })
const hiddenTags = computed(() => (Object.keys(tagFilter.value) as Tags[]).filter((k) => !tagFilter.value[k]))

const flags = ref<FeatureFlag[]>([])

const filteredFlags = computed(() =>
  flags.value.filter((f) => {
    const visibleTags = f.tags.filter((t) => !hiddenTags.value.includes(t.value))
    return 'localValue' in f || visibleTags.length > 0
  }),
)

const fetchFlags = async () => {
  const { data } = await oldApi.get(`office/account-feature-flags/${session.account.id}`).json<{ data: any }>()
  flags.value = data
    .sort((a: any, b: any) => a.name.localeCompare(b.name))
    .map(({ accountOverride: o, tag, ...f }: any) => {
      let accountTooltip: string | null = null
      if (o) {
        const action = f.value ? 'Enabled' : 'Disabled'
        const user = (o.updatedBy || o.createdBy)?.name
        const on = dayjs(o.created).format('YYYY-MM-DD')
        accountTooltip = `${action} for account by ${user}: "${o.reason}" on ${on}`
      }

      const tags = (tag || '').split(',').map((value: Tags) => {
        let tooltip = tagTooltips[value]
        if (value === 'plan' && f.inPlan) tooltip = `Included in plan '${f.inPlan}'`
        if (value === 'addon' && f.inAddon) tooltip = `Included in addon '${f.inAddon}'`
        return { value, tooltip }
      })

      const res = { ...f, tags, accountTooltip } as FeatureFlag
      const flagName = f.name as keyof Features
      if (session.features[flagName] !== f.value) res.localValue = session.features[flagName]
      return res
    })
}

const setOverride = (name: string, value: boolean) => {
  const flag = flags.value.find((f) => f.name === name)
  if (!flag) throw new Error('invalid flag name')
  if (flag.value === value) {
    delete flag.localValue
  } else {
    flag.localValue = value
  }
}

const hasChanges = computed(
  () => flags.value.filter((f) => ('localValue' in f ? f.localValue : f.value) != session.features[f?.name]).length > 0,
)

const save = () => {
  const overrides = Object.fromEntries(flags.value.filter((f) => 'localValue' in f).map((f) => [f.name, f.localValue]))
  writeOverrides(overrides)
  location.reload()
}

onBeforeMount(() => fetchFlags())

return (_ctx: any,_cache: any) => {
  const _directive_tippy = _resolveDirective("tippy")!

  return (_openBlock(), _createBlock(QModal, {
    show: "",
    wide: "",
    dismissable: "",
    onClose: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('close')))
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.$t('flag_manager.title')), 1 /* TEXT */),
        _createElementVNode("span", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tagFilter.value, (_, tag) => {
            return _withDirectives((_openBlock(), _createBlock(_unref(UiTag), {
              key: tag,
              class: _normalizeClass([!tagFilter.value[tag] && 'line-through opacity-50', 'mx-1 cursor-pointer']),
              theme: tagColors[tag],
              onClick: ($event: any) => (tagFilter.value[tag] = !tagFilter.value[tag])
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(tag), 1 /* TEXT */)
              ]),
              _: 2 /* DYNAMIC */
            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["class", "theme", "onClick"])), [
              [_directive_tippy, tagTooltips[tag]]
            ])
          }), 128 /* KEYED_FRAGMENT */))
        ])
      ])
    ]),
    actions: _withCtx(() => [
      _createVNode(_unref(UiButton), {
        secondary: "",
        class: "mr-3",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('universal.cancel')), 1 /* TEXT */)
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(_unref(UiButton), {
        disabled: !hasChanges.value,
        onClick: _cache[1] || (_cache[1] = ($event: any) => (save()))
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('flag_manager.save')), 1 /* TEXT */)
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["disabled"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_4, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredFlags.value, (feat) => {
          return (_openBlock(), _createElementBlock("div", {
            key: feat.name,
            class: "mb-4 flex w-full break-inside-avoid-column"
          }, [
            _withDirectives(_createVNode(_unref(UiPillToggle), {
              class: _normalizeClass(['localValue' in feat && '[&_input+span]:!bg-yellow-40', "mr-3"]),
              "model-value": 'localValue' in feat ? feat.localValue : feat.value,
              "onUpdate:modelValue": ($event: any) => (setOverride(feat.name, $event))
            }, null, 8 /* PROPS */, ["class", "model-value", "onUpdate:modelValue"]), [
              [_directive_tippy, { content: 'localValue' in feat && 'Locally overidden', arrow: false, offset: [0, 5] }]
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                (feat.accountTooltip)
                  ? _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_7, _cache[3] || (_cache[3] = [
                      _createTextVNode("🏢 ", -1 /* CACHED */)
                    ]))), [
                      [_directive_tippy, { content: feat.accountTooltip, maxWidth: 500 }]
                    ])
                  : _createCommentVNode("v-if", true),
                _withDirectives((_openBlock(), _createElementBlock("span", null, [
                  _createTextVNode(_toDisplayString(feat.name), 1 /* TEXT */)
                ])), [
                  [_directive_tippy, feat.description]
                ])
              ]),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(feat.tags, (t) => {
                return _withDirectives((_openBlock(), _createBlock(_unref(UiTag), {
                  key: t.value,
                  theme: tagColors[t.value]
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(t.value), 1 /* TEXT */)
                  ]),
                  _: 2 /* DYNAMIC */
                }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["theme"])), [
                  [_directive_tippy, t.tooltip]
                ])
              }), 128 /* KEYED_FRAGMENT */))
            ])
          ]))
        }), 128 /* KEYED_FRAGMENT */))
      ])
    ]),
    _: 1 /* STABLE */
  }))
}
}

})